'use client';

import { TransactionDirection } from '@bts-web/data-layer/server';
import { css } from '@bts-web/utils-style-engine';
import { TransactionTypes } from '../types/transactions';
import { ReactElement, ReactNode } from 'react';
import { RegularCheckmark } from '../../common/svgIcons';

export type TransactionCellElemProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> & {
  visual?: 'default' | 'success' | 'warning' | 'error';
  startIconSlot?: React.ReactNode;
  endIconSlot?: React.ReactNode;
  titleChipContent?: React.ReactNode;
  transactionType?: TransactionTypes;
  direction?: TransactionDirection;
} & {
  id: string;
  tagContent?: ReactNode | null;
  titleChipContent?: ReactNode;
  transactionBadgesSlot?: ReactElement;
  title?: ReactNode;
  subtitle?: string;
  disabled?: boolean;
  type?: HTMLButtonElement['type'];
  fiatAmountText?: string;
  assetAmountText?: string;
  transactionStatus?: string;
  selected?: boolean;
  onClick?: () => void;
  transactionType?: TransactionTypes;
  direction?: TransactionDirection;
  displaySecondIconSlot?: boolean;
};

const getColorForVisual = (visual: string | undefined) => {
  switch (visual) {
    case 'success':
      return {
        moneyAmountColor: 'neutrals.text_primary',
        statusColor: 'positive.text_primary',
      };

    case 'warning':
      return {
        moneyAmountColor: 'neutrals.text_primary',
        statusColor: 'warning.text_primary',
      };

    case 'error':
      return {
        moneyAmountColor: 'neutrals.text_disabled',
        statusColor: 'negative.text_primary',
      };

    default:
      return {
        moneyAmountColor: 'neutrals.text_primary',
        statusColor: 'neutrals.text_primary',
      };
  }
};

const ThemedTransactionCell = ({
  startIconSlot,
  endIconSlot,
  titleChipContent,
  visual = 'default',
  displaySecondIconSlot = true,
  assetAmountText,
  subtitle,
  fiatAmountText,
  title,
  transactionStatus,
  transactionBadgesSlot,
  disabled,
  onClick,
  tagContent,
  id,
  transactionType,
  selected,
}: TransactionCellElemProps) => {
  const { moneyAmountColor, statusColor } = getColorForVisual(visual);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        fontSize: 'body.medium',
        color: 'neutrals.text_secondary',
        borderBottomWidth: '1px',
        borderBottomColor: 'neutrals.divider',
        width: '100%',
        py: 'small',
        ...(!displaySecondIconSlot ? { paddingRight: '28px' } : {}),
        '&[data-state="active"]': {
          _hover: {
            cursor: 'pointer',
          },
          _active: {
            backgroundColor: 'neutrals.fill_focused',
          },
        },
        '& [data-element="details"]': {
          display: 'flex',
          gap: 'extra_small_2',
          justifyContent: 'space-between',
        },
        '& [data-element="title"]': {
          fontSize: 'label.medium_medium',
          color: 'neutrals.text_primary',
        },
        '& [data-element="markdown-style"]': {
          fontSize: 'label.medium_bold',
          fontWeight: 'label.medium_bold',
          lineHeight: 'label.medium_bold',
          letterSpacing: 'label.medium_bold',
        },
        '& [data-element="first-icon-slot"]': {
          color: 'neutrals.text_primary',
          display: 'flex',
          alignItems: 'center',
        },
        '& [data-element="details-first-half"]': {
          display: 'flex',
          alignItems: 'center',
          gap: 'small',
          minWidth: 0,
          '& div:nth-child(2)': {
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_2',
            minWidth: 0,
            '& > span': {
              wordBreak: 'normal',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textAlign: 'initial', // override the button element default text textAlign="center" value
            },
          },
        },
        '& [data-element="details-second-half"]': {
          display: 'flex',
          gap: 'extra_small_2',
          '& div:first-child': {
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_2',
            justifyContent: 'center',
            alignItems: 'end',
            '& > span': {
              whiteSpace: 'nowrap',
            },
          },
        },
        '& [data-element="second-icon-slot"]': {
          display: 'flex',
          marginInlineStart: 'auto',
          alignItems: 'center',
          minWidth: '20px',
        },
        '& [data-element="badges"]': {
          paddingInlineStart: 'extra_large',
        },
      })}
      data-testid="transaction-cell"
      aria-label={`${title} ${transactionType} ${id}`}
      {...(!disabled && onClick ? { 'data-state': 'active', onClick } : {})}
    >
      <div data-element="details">
        <div data-element="details-first-half">
          {startIconSlot && (
            <div data-element="first-icon-slot">{startIconSlot}</div>
          )}

          <div>
            <span data-element="title">
              {title} {titleChipContent}
            </span>

            {subtitle && <span>{subtitle}</span>}

            {tagContent && tagContent}
          </div>
        </div>

        <div data-element="details-second-half">
          <div>
            {fiatAmountText && (
              <span
                className={css({
                  fontSize: 'label.medium_medium',
                  color: moneyAmountColor,
                })}
              >
                {fiatAmountText}
              </span>
            )}

            {assetAmountText && <span>{assetAmountText}</span>}

            {transactionStatus && (
              <span
                data-testid="transaction-cell__status"
                className={css({
                  color: statusColor,
                })}
              >
                {transactionStatus}
              </span>
            )}
          </div>

          <div data-element="second-icon-slot">{endIconSlot}</div>
          {selected && <RegularCheckmark data-element="selected-icon" />}
        </div>
      </div>

      {transactionBadgesSlot && (
        <div data-element="badges">{transactionBadgesSlot}</div>
      )}
    </div>
  );
};

export { ThemedTransactionCell as TransactionCell };
